import type { CombatActor, CombatState } from './index.ts';
import { findPath, hasLineOfSight, metricDistance, neighbours, type NavigationMap, type NavPosition, type PathStep } from '../navigation/index.ts';

export const sameCell = (a: NavPosition, b: NavPosition): boolean => a.x === b.x && a.y === b.y && a.layer === b.layer;
export const occupied = (state: Readonly<CombatState>, id: string): NavPosition[] =>
  Object.values(state.actors).filter(a => a.id !== id && a.status === 'alive' && !state.tactics?.actors[a.id]?.escaped).map(a => a.position);
export const opponents = (state: Readonly<CombatState>, self: CombatActor): CombatActor[] =>
  Object.values(state.actors).filter(a => a.status === 'alive' && !state.tactics?.actors[a.id]?.escaped && (a.role === 'enemy') !== (self.role === 'enemy'))
    .sort((a, b) => (metricDistance(self.position, a.position) ?? Number.MAX_SAFE_INTEGER) - (metricDistance(self.position, b.position) ?? Number.MAX_SAFE_INTEGER) || (a.id < b.id ? -1 : 1));

export function approach(state: Readonly<CombatState>, map: NavigationMap, self: CombatActor, target: CombatActor, range: number): PathStep | null {
  const distance = metricDistance(self.position, target.position);
  if (distance !== null && distance <= range && hasLineOfSight(map, self.position, target.position)) return null;
  const blocked = occupied(state, self.id);
  let best: PathStep[] | null = null;
  let cost = Number.MAX_SAFE_INTEGER;
  // ponytail: scan authored cells for the cheapest firing position; cache a nav graph if zones outgrow this small encounter scale.
  for (const layer of map.layers) for (const cell of layer.cells) {
    const position = { x: cell.x, y: cell.y, layer: layer.id };
    const gap = metricDistance(position, target.position);
    if (gap === null || gap > range || !hasLineOfSight(map, position, target.position)) continue;
    const path = findPath(map, self.position, position, blocked);
    if (!path?.length) continue;
    const nextCost = path.reduce((sum, step) => sum + step.cost, 0);
    if (nextCost < cost) { cost = nextCost; best = path; }
  }
  return best?.[0] ?? null;
}

export function saferStep(state: Readonly<CombatState>, map: NavigationMap, self: CombatActor): NavPosition | null {
  const foes = opponents(state, self);
  const safety = (p: NavPosition): number => Math.min(...foes.map(f => metricDistance(p, f.position) ?? Number.MAX_SAFE_INTEGER));
  const blocked = occupied(state, self.id);
  let best = self.position;
  for (const step of neighbours(map, self.position)) {
    if (blocked.some(p => sameCell(p, step.position))) continue;
    if (safety(step.position) > safety(best)) best = step.position;
  }
  return sameCell(best, self.position) ? null : best;
}
